import { Box, ThemeProvider } from '@mui/material';
import { useIntl } from 'react-intl';
import { HangingProtocol } from '../binding/hanging';
import {
    consoleFieldFrameSx,
    CONSOLE_CONTROL_GAP,
    consoleGroupSx,
    FOOTER_CORNER_SX,
    GUTTER_PX,
    LEFT_EDGE_PANE_STYLE,
    ROW_FOOTER_SX,
} from '../chrome';
import { chromeTheme } from '../chromeTheme';
import { hangTemplate } from '../layout/layout';
import { DatasetRow, ViewportId } from '../layout/types';
import ImageNameTrigger from '../tile/ImageNameTrigger';
import { AddRowButton } from '../tile/RowFooter';
import RowLayoutMenu from '../tile/RowLayoutMenu';
import { tileFrameSx } from '../tile/tileFrame';
import TileTitleBar, { TitlePlaneLabel } from '../tile/TileTitleBar';
import { EmptyWindowLevelValues } from '../tile/WindowLevelValues';

interface EmptyViewportGridProps {
    /** The shape the grid hangs once a dataset arrives. */
    protocol: HangingProtocol;
}

/** The handler of a disabled control, which never fires. */
function noop(): void {}

/** The protocol's image panes hung in a row that no dataset is bound to. */
function unboundRowOf(protocol: HangingProtocol): DatasetRow {
    let next = 0;
    return {
        dataset: undefined,
        heightPercent: 100,
        ...hangTemplate(
            protocol.template,
            () => `empty-${(next += 1)}` as ViewportId
        ),
    };
}

/**
 * The grid in the shape it will hang, with every pane empty: what the viewer
 * shows while the patient has no dataset to draw. The row keeps the console
 * chrome of a bound row: each pane a tile frame whose title bar names its
 * plane and carries the tile's maximize control, and under them the row's
 * footer with its image, window, blend, layout and add-row controls. Every
 * control is shown disabled; the grid draws no image and takes no input.
 * With no image there are no planes to cycle, so no plane control is shown.
 */
export default function EmptyViewportGrid({
    protocol,
}: EmptyViewportGridProps) {
    const intl = useIntl();
    const row = unboundRowOf(protocol);
    return (
        <ThemeProvider theme={chromeTheme}>
            <Box
                data-testid="grid-render-unavailable"
                aria-hidden
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                }}
            >
                {/* The same margins as the bound grid, so the row ends on the
                    sidebar's line and stands off the window's right edge. */}
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        mb: 1,
                        mr: 1,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            display: 'grid',
                            gridTemplateColumns: row.columnPercents
                                .map((percent) => `${percent}fr`)
                                .join(' '),
                            gridTemplateRows: row.rowPercents
                                .map((percent) => `${percent}fr`)
                                .join(' '),
                            gap: `${GUTTER_PX}px`,
                        }}
                    >
                        {row.cells.map(({ id, content, placement }) => (
                            <Box
                                key={id}
                                data-testid="empty-tile"
                                style={
                                    placement.colStart === 1
                                        ? LEFT_EDGE_PANE_STYLE
                                        : undefined
                                }
                                sx={{
                                    ...tileFrameSx(false),
                                    gridColumn: `${placement.colStart} / span ${placement.colSpan}`,
                                    gridRow: `${placement.rowStart} / span ${placement.rowSpan}`,
                                }}
                            >
                                <TileTitleBar>
                                    <Box sx={{ flex: 1 }} />
                                    {content.kind === 'plane' ? (
                                        <TitlePlaneLabel
                                            plane={content.plane}
                                        />
                                    ) : null}
                                </TileTitleBar>
                            </Box>
                        ))}
                    </Box>
                    <Box data-testid="empty-row-footer" sx={ROW_FOOTER_SX}>
                        <Box
                            sx={{
                                ...consoleGroupSx(CONSOLE_CONTROL_GAP),
                                flex: 1,
                            }}
                        >
                            <ImageNameTrigger
                                label={undefined}
                                placeholder={intl.formatMessage({
                                    id: 'rowImage.none',
                                })}
                                ariaLabel={intl.formatMessage({
                                    id: 'rowImage.label',
                                })}
                                open={false}
                                disabled
                                onOpen={noop}
                                testId="empty-row-image-trigger"
                            />
                            <Box sx={consoleFieldFrameSx(false)}>
                                <EmptyWindowLevelValues />
                            </Box>
                            <ImageNameTrigger
                                label={undefined}
                                placeholder={intl.formatMessage({
                                    id: 'fusion.blendPlaceholder',
                                })}
                                ariaLabel={intl.formatMessage({
                                    id: 'fusion.addFusedImage',
                                })}
                                open={false}
                                disabled
                                onOpen={noop}
                                testId="empty-pair-image-trigger"
                            />
                        </Box>
                        <RowLayoutMenu
                            shape={protocol.template}
                            onSelect={noop}
                            disabled
                        />
                        <Box sx={FOOTER_CORNER_SX}>
                            <AddRowButton />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </ThemeProvider>
    );
}
